import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { Search, Filter, Users } from 'lucide-react';

// ✅ Helper function to get badge colors dynamically
function getStatusColor(status) {
  switch (status) {
    case 'Checked In': return 'success';
    case 'Checked Out': return 'secondary';
    case 'Verified': return 'primary';
    case 'Not Verified': return 'warning';
    default: return 'secondary';
  }
}

// ✅ Mock Guest Data (50 guests)
const mockGuests = Array.from({ length: 50 }, (_, i) => ({
  id: i + 1,
  name: [
    'Chinedu Okafor', 'Aisha Bello', 'Tunde Balogun', 'Ngozi Adeyemi',
    'Ibrahim Lawal', 'Funke Ajayi', 'Sola Adekunle', 'Bisi Onabanjo',
    'Yakubu Garba', 'Halima Yusuf'
  ][Math.floor(Math.random() * 10)],
  date: new Date(2025, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
  method: ['NIN', 'BVN', 'Passport', 'Driver’s License'][Math.floor(Math.random() * 4)],
  checkStatus: Math.random() > 0.5 ? 'Checked In' : 'Checked Out',
  verifyStatus: Math.random() > 0.5 ? 'Verified' : 'Not Verified',
  img: require('../assets/user1.png') // ✅ you can change to user2.png etc.
}));

export default function GuestListPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [tab, setTab] = useState('Active'); // Active or Checked Out
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;

  // ✅ Filter guests based on tab & search
  const filteredGuests = mockGuests.filter(guest =>
    (tab === 'Active' ? guest.checkStatus === 'Checked In' : guest.checkStatus === 'Checked Out') &&
    guest.name.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ Pagination
  const totalPages = Math.ceil(filteredGuests.length / perPage);
  const displayedGuests = filteredGuests.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="d-flex vh-100 overflow-hidden">
      {/* ✅ Sidebar */}
      <Sidebar
        active="guests"
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
      />

      {/* ✅ Main Content */}
      <div className="flex-grow-1 d-flex flex-column bg-light">
        <Header 
          darkMode={darkMode} 
          setDarkMode={setDarkMode} 
          toggleSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          notifications={4}   
        />               
        
        {/* ✅ Modern Top Bar */}
        <div className="p-4">
          <div className="d-flex align-items-center justify-content-between bg-white rounded p-3">
            <h4 className="m-0 fw-bold cool-font">Guest List</h4>
            <div className="d-flex align-items-center gap-2">
              
              {/* Modern Search Box */}
              <div className="position-relative">
                <Search 
                  size={18} 
                  className="position-absolute top-50 start-2 translate-middle-y text-muted" 
                  style={{ left: '10px' }} 
                />
                <input
                  type="text"
                  className="form-control ps-5 rounded-pill"
                  placeholder="Search guests..."
                  style={{ width: '250px', transition: '0.3s ease all' }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Filter Button */}
              <button className="btn btn-outline-primary rounded-pill px-3 d-flex align-items-center gap-1">
                <Filter size={16} /> Filter
              </button>
            </div>
          </div>
        </div>

        {/* ✅ Main Scrollable Area */}
        <main className="flex-grow-1 overflow-auto p-4">

          {/* ✅ Switcher */}
          <div className="d-flex justify-content-center mb-4">
            <div className="btn-group w-50">
              <button
                className={`btn ${tab === 'Active' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setTab('Active'); setPage(1); }}
              >
                Checked In Guests
              </button>
              <button
                className={`btn ${tab === 'Checked Out' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setTab('Checked Out'); setPage(1); }}
              >
                Checked Out Guests
              </button>
            </div>
          </div>

          {/* ✅ Empty State */}
          {displayedGuests.length === 0 ? (
            <div className="text-center mt-5">
              <Users size={64} className="text-muted mb-3" />
              <h5 className="fw-bold">No Guests Found</h5>
              <p className="text-muted">Try adjusting your filters or search term.</p>
              <button 
                className="btn btn-primary mt-2"
                onClick={() => setSearch('')}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              {/* ✅ Guest Cards */}
              <div className="row g-3">
                {displayedGuests.map((guest) => (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={guest.id}>
                    <div className="card border h-100 text-center p-3">

                      {/* 🖼 Image */}
                      <div
                        className="mx-auto rounded overflow-hidden mb-3"
                        style={{ width: '120px', height: '120px', background: '#f0f0f0' }}
                      >
                        <img
                          src={guest.img}
                          alt={guest.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>

                      {/* 📝 Details */}
                      <h6 className="fw-bold">{guest.name}</h6>
                      <p className="small text-muted mb-1">{guest.date}</p>
                      <p className="small text-muted mb-2"><strong>Method:</strong> {guest.method}</p>

                      {/* ✅ Status Badges */}
                      <div className="mb-2">
                        <span className={`badge bg-${getStatusColor(guest.checkStatus)} me-1`}>
                          {guest.checkStatus}
                        </span>
                        <span className={`badge bg-${getStatusColor(guest.verifyStatus)}`}>
                          {guest.verifyStatus}
                        </span>
                      </div>

                      {/* 🔘 Button */}
                      <button className="btn btn-primary btn-sm w-100 mt-auto">View Guest</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* ✅ Pagination */}
              <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  ‹ Prev
                </button>
                <span className="fw-bold">{page} / {totalPages}</span>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next ›
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
